/**
 * DataQualityScorecard - React wrapper for existing Data Quality UI
 * ============================================================================
 *
 * DESIGN DECISION: Instead of rewriting the entire Data Quality UI in React,
 * this component acts as a thin wrapper that:
 * 1. Fetches results from the Data Quality API
 * 2. Injects the existing displayResults() function from dataQuality.js
 * 3. Reuses all existing styles, animations, and interactions
 *
 * This approach:
 * - Maintains UX consistency across standalone and Journey modes
 * - Reduces implementation time from weeks to hours
 * - Eliminates risk of introducing new bugs
 * - Leverages battle-tested code (dataQuality.js is production-proven)
 *
 * Related Files:
 * - public/js/dataQuality.js (Core rendering logic)
 * - public/js/dataQuality-enhancements.js (Enhanced visualizations)
 * - public/styles-dataQuality.css (Styles)
 * - public/styles-dataQuality-enhancements.css (Enhanced styles)
 *
 * @author Claude Code
 * @date 2025-10-30
 * ============================================================================
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import DQEnhancementsPanel from './DQEnhancementsPanel';
import AnomalyDetectionPanel from './AnomalyDetectionPanel';
import DriftDetectionPanel from './DriftDetectionPanel';
import TextualQualityPanel from './TextualQualityPanel';
import { DataQualityCategoryDrawer } from '../dataQuality/DataQualityCategoryDrawer';
import { useDQCategoryDrawer } from '../dataQuality/useDQCategoryDrawer';
import type { DQCategoryClickEventDetail } from '../../types/dataQualityCategory';
import { isValidCategoryId } from '../../types/dataQualityCategory';

// DQ Enhancement types matching DQEnhancementsPanel expectations
type Severity = 'none' | 'low' | 'medium' | 'high';

interface DisguisedMissingColumnResult {
  column: string;
  patterns_detected: Array<{
    pattern: string;
    count: number;
    percentage: number;
  }>;
  total_disguised: number;
  disguised_percentage: number;
  severity: Severity;
  recommendation: string;
}

interface DisguisedMissingReport {
  analyzed_at: string;
  columns_analyzed: number;
  columns_with_disguised: number;
  column_results: DisguisedMissingColumnResult[];
  overall_severity: Severity;
  summary: string;
}

interface NearDuplicateCluster {
  cluster_id: number;
  record_indices: number[];
  representative_index: number;
  avg_intra_similarity: number;
}

interface NearDuplicatePair {
  index1: number;
  index2: number;
  similarity: number;
  matching_columns: string[];
}

interface NearDuplicateResult {
  analyzed_at: string;
  total_records: number;
  comparison_columns: string[];
  similarity_threshold: number;
  duplicate_pairs: NearDuplicatePair[];
  clusters: NearDuplicateCluster[];
  records_in_clusters: number;
  cluster_count: number;
  duplicate_rate: number;
  severity: Severity;
  recommendation: string;
}

interface OutlierPoint {
  index: number;
  value: number;
  monnad_score?: number;
  rilof_score?: number;
  is_outlier_monnad?: boolean;
  is_outlier_rilof?: boolean;
  is_consensus_outlier?: boolean;
}

interface RobustOutlierColumnResult {
  column: string;
  monnad: {
    outlier_count: number;
    outlier_percentage: number;
  };
  rilof: {
    outlier_count: number;
    outlier_percentage: number;
  };
  consensus_outliers: OutlierPoint[];
  consensus_count: number;
  consensus_percentage: number;
  severity: Severity;
  recommendation: string;
}

interface RobustOutlierReport {
  analyzed_at: string;
  columns_analyzed: number;
  columns_with_outliers: number;
  total_consensus_outliers: number;
  column_results: RobustOutlierColumnResult[];
  overall_severity: Severity;
  summary: string;
}

export interface DataQualityResults {
  job_id?: string;
  status: 'completed' | 'processing' | 'failed';
  dataset_name: string;
  analyzed_at: string;
  overall_score: number;
  rating: 'critical' | 'poor' | 'good' | 'excellent';
  component_scores: {
    information_content: number;
    statistical_quality: number;
    redundancy: number;
    outliers: number;
    sparsity: number;
  };
  issues: {
    critical: Array<{ message: string; action: string; column?: string }>;
    warnings: Array<{ message: string; action: string; column?: string }>;
    passed: string[];
  };
  recommendations: Array<{
    priority: string;
    title: string;
    description: string;
    impact_score?: number;
  }>;
  columns: Record<string, unknown>[];
  benfords_law_results?: Record<string, unknown>[];
  mutual_information?: {
    top_redundant_pairs: Record<string, unknown>[];
    heatmap_data?: number[][];
  };
  // DQ Enhancements (Sprint 9)
  dq_enhancements?: {
    disguised_missing?: DisguisedMissingReport;
    near_duplicates_jw?: NearDuplicateResult;
    robust_outliers?: RobustOutlierReport;
    overall_severity?: Severity;
    summary?: string;
  };
  textual_quality?: Record<string, unknown>;
  metadata: {
    total_rows: number;
    total_columns: number;
    numeric_columns: number;
    categorical_columns: number;
    analysis_time_ms: number;
    sampling_applied: boolean;
  };
}

interface DataQualityScorecardProps {
  jobId: string;
  journeyId?: string;
  onError?: (error: string) => void;
  onRetry?: () => void;
}

type EnhancedTab = 'enhancements' | 'anomaly' | 'drift' | 'textual';

export const DataQualityScorecard: React.FC<DataQualityScorecardProps> = ({
  jobId,
  journeyId,
  onError,
  onRetry,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<DataQualityResults | null>(null);
  const [activeEnhancedTab, setActiveEnhancedTab] = useState<EnhancedTab>('enhancements');

  // Category Drawer Hook
  const {
    isOpen: isDrawerOpen,
    categoryDetail,
    loading: drawerLoading,
    error: drawerError,
    openDrawer,
    closeDrawer,
  } = useDQCategoryDrawer(jobId);

  // Handle category info button clicks from vanilla JS
  const handleCategoryClick = useCallback((event: CustomEvent<DQCategoryClickEventDetail>) => {
    const { categoryId } = event.detail;
    if (isValidCategoryId(categoryId)) {
      openDrawer(categoryId);
    } else {
      console.warn('[DataQualityScorecard] Invalid category ID:', categoryId);
    }
  }, [openDrawer]);

  // Listen for dq-category-click events from vanilla JS
  useEffect(() => {
    window.addEventListener('dq-category-click', handleCategoryClick as EventListener);
    return () => {
      window.removeEventListener('dq-category-click', handleCategoryClick as EventListener);
    };
  }, [handleCategoryClick]);

  // Bind the dataQuality.js document-click delegation ONCE per page load.
  // initDataQuality() is never called in SPA mode (only displayResults), so without
  // this the .dq-category-info-btn clicks never fire dq-category-click events and
  // the category drawer is dead. DC-1549 redo follow-up (2026-05-12).
  useEffect(() => {
    type DQDisplayShape = { attachDataQualityEventListeners?: () => void };
    const w = window as unknown as { DQDisplay?: DQDisplayShape; __dqListenersAttached?: boolean };
    if (w.__dqListenersAttached) return;
    if (typeof w.DQDisplay?.attachDataQualityEventListeners === 'function') {
      w.DQDisplay.attachDataQualityEventListeners();
      w.__dqListenersAttached = true;
    }
  }, []);

  // DATA-INTEGRITY: tracks the job whose poll loop is currently allowed to write
  // state. When jobId changes, the previous self-rescheduling poll keeps running
  // against the OLD job (closure) and, on completion, would overwrite the current
  // job's scorecard with stale results. Each poll checks it is still the active
  // job before scheduling a retry or committing results.
  const activeJobIdRef = useRef(jobId);
  useEffect(() => {
    activeJobIdRef.current = jobId;
    fetchResults(jobId);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchResults is defined in component
  }, [jobId]);

  const fetchResults = async (thisJobId: string) => {
    // Bail if a newer job has taken over since this poll was scheduled.
    if (activeJobIdRef.current !== thisJobId) return;
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/data-quality/${thisJobId}/results`);

      if (activeJobIdRef.current !== thisJobId) return; // superseded mid-flight

      if (!response.ok) {
        throw new Error(`Failed to fetch results: ${response.statusText}`);
      }

      const data = await response.json();

      if (activeJobIdRef.current !== thisJobId) return; // superseded mid-flight

      if (data.status === 'failed') {
        throw new Error(data.error || 'Analysis failed');
      }

      if (data.status === 'processing') {
        // Still processing, retry in 2 seconds (only for the active job)
        setTimeout(() => fetchResults(thisJobId), 2000);
        return;
      }

      // Results are ready
      setResults(data);
      setLoading(false);

      // Wait for DOM to render, then inject into the existing Data Quality display function
      setTimeout(() => {
        if (activeJobIdRef.current !== thisJobId) return;
        renderWithExistingUI(data);
      }, 100);

    } catch (err: unknown) {
      if (activeJobIdRef.current !== thisJobId) return; // don't surface a stale job's error
      console.error('[DataQualityScorecard] Failed to fetch results:', err);
      const errMessage = err instanceof Error ? err.message : 'Failed to fetch results';
      setError(errMessage);
      setLoading(false);
      onError?.(errMessage);
    }
  };

  /**
   * Render results using the existing dataQuality.js displayResults() function
   * This maintains 100% UX consistency with the standalone Data Quality feature
   */
  const renderWithExistingUI = (resultsData: DataQualityResults) => {
    if (!containerRef.current) return;

    // Create a temporary container div with the expected ID
    const resultsContainer = document.createElement('div');
    resultsContainer.id = 'dq-results-container';
    containerRef.current.appendChild(resultsContainer);

    // Use the global DQDisplay functions from dataQuality.js
    const DQDisplay = (window as unknown as { DQDisplay?: { displayResults?: (data: DataQualityResults) => void; buildDashboardHTML?: (data: DataQualityResults) => string } }).DQDisplay;

    if (DQDisplay && typeof DQDisplay.displayResults === 'function') {
      // Use the existing displayResults function (preferred method)
      DQDisplay.displayResults(resultsData);
    } else if (DQDisplay && typeof DQDisplay.buildDashboardHTML === 'function') {
      // Fallback: manually build HTML using existing function
      const html = DQDisplay.buildDashboardHTML(resultsData);
      resultsContainer.innerHTML = html;

      // Trigger animations if enhancements loaded
      const DQEnhancements = (window as unknown as { DQEnhancements?: { animateRadialChart?: () => void; animateComponentBars?: () => void; animateColumnScoreBars?: () => void } }).DQEnhancements;
      if (DQEnhancements) {
        setTimeout(() => {
          DQEnhancements.animateRadialChart?.();
          DQEnhancements.animateComponentBars?.();
          DQEnhancements.animateColumnScoreBars?.();
        }, 100);
      }
    } else {
      // Final fallback: render a simple scorecard (should never happen in production)
      console.warn('[DataQualityScorecard] DQDisplay not found, using fallback rendering');
      renderFallbackScorecard(resultsData);
    }
  };

  /**
   * Fallback rendering if dataQuality.js is not loaded
   * This should never happen in production, but provides graceful degradation
   */
  const renderFallbackScorecard = (resultsData: DataQualityResults) => {
    if (!containerRef.current) return;

    const { overall_score, rating, issues, metadata } = resultsData;
    const criticalCount = issues.critical?.length || 0;
    const warningsCount = issues.warnings?.length || 0;

    containerRef.current.innerHTML = `
      <div class="dq-dashboard-card">
        <div class="dq-header">
          <div class="dq-header-left">
            <h2 class="dq-title">Data Quality Assessment</h2>
            <p class="dq-metadata">
              <span>${resultsData.dataset_name}</span>
              <span class="dq-separator">•</span>
              <span>${metadata.total_rows.toLocaleString()} rows × ${metadata.total_columns} columns</span>
            </p>
          </div>
        </div>

        <div class="dq-score-container">
          <div class="dq-score-label">Overall Data Quality</div>
          <div class="dq-score-value-wrapper">
            <span class="dq-score-value dq-score-${rating}">${overall_score}</span>
            <span class="dq-score-max">/100</span>
            <span class="dq-score-badge dq-badge-${rating}">${rating.toUpperCase()}</span>
          </div>
          <div class="dq-score-bar" role="progressbar" aria-valuenow="${overall_score}" aria-valuemin="0" aria-valuemax="100">
            <div class="dq-score-fill dq-score-fill-${rating}" style="width: ${overall_score}%;"></div>
          </div>
        </div>

        <div class="dq-issue-summary">
          <div class="dq-issue-stat dq-issue-critical">
            <span class="dq-issue-count">${criticalCount}</span>
            <span class="dq-issue-label">Critical Issues</span>
          </div>
          <div class="dq-issue-stat dq-issue-warnings">
            <span class="dq-issue-count">${warningsCount}</span>
            <span class="dq-issue-label">Warnings</span>
          </div>
          <div class="dq-issue-stat dq-issue-passed">
            <span class="dq-issue-count">${issues.passed?.length || 0}</span>
            <span class="dq-issue-label">Passed Checks</span>
          </div>
        </div>

        <div class="dq-actions">
          <button class="btn-primary" onclick="alert('Full Data Quality UI not loaded. Please refresh the page.')">
            View Full Report
          </button>
        </div>
      </div>
    `;
  };

  if (loading) {
    return (
      <div ref={containerRef} className="data-quality-scorecard loading">
        <div className="dq-dashboard-card">
          <div className="dq-loading-state">
            <div className="dq-spinner"></div>
            <p>Loading data quality results...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div ref={containerRef} className="data-quality-scorecard error">
        <div className="dq-dashboard-card">
          <div className="dq-error-state">
            <h3>⚠️ Failed to Load Results</h3>
            <p>{error}</p>
            {onRetry && (
              <button className="btn-primary" onClick={onRetry}>
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main render: Container for existing UI injection
  return (
    <div className="data-quality-scorecard-wrapper">
      <div
        ref={containerRef}
        className="data-quality-scorecard"
        data-job-id={jobId}
        data-testid="data-quality-scorecard"
      >
        {/* Existing dataQuality.js UI will be injected here */}
      </div>

      {/* Enhanced Data Quality Analysis Panels (Sprint 9) */}
      {results && (
        <div className="dq-enhancements-section" style={{ marginTop: '2rem' }}>
          {/* Tab Navigation */}
          <div className="dq-enhanced-tabs" style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '1rem',
            borderBottom: '1px solid var(--tri-taupe)',
            paddingBottom: '0.5rem'
          }}>
            <button
              onClick={() => setActiveEnhancedTab('enhancements')}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                background: activeEnhancedTab === 'enhancements' ? 'var(--tri-teal)' : 'var(--tri-card-2)',
                color: activeEnhancedTab === 'enhancements' ? 'white' : 'var(--tri-ink)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Data Enhancements
            </button>
            <button
              onClick={() => setActiveEnhancedTab('anomaly')}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                background: activeEnhancedTab === 'anomaly' ? 'var(--tri-teal)' : 'var(--tri-card-2)',
                color: activeEnhancedTab === 'anomaly' ? 'white' : 'var(--tri-ink)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Anomaly Detection
            </button>
            <button
              onClick={() => setActiveEnhancedTab('drift')}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                background: activeEnhancedTab === 'drift' ? 'var(--tri-teal)' : 'var(--tri-card-2)',
                color: activeEnhancedTab === 'drift' ? 'white' : 'var(--tri-ink)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Drift Detection
            </button>
            <button
              onClick={() => setActiveEnhancedTab('textual')}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                background: activeEnhancedTab === 'textual' ? 'var(--tri-teal)' : 'var(--tri-card-2)',
                color: activeEnhancedTab === 'textual' ? 'white' : 'var(--tri-ink)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Textual Quality
            </button>
          </div>

          {/* Tab Content */}
          {activeEnhancedTab === 'enhancements' && (
            <DQEnhancementsPanel
              journeyId={journeyId}
              results={results?.dq_enhancements ? {
                disguised_missing: results.dq_enhancements.disguised_missing,
                near_duplicates_jw: results.dq_enhancements.near_duplicates_jw,
                robust_outliers: results.dq_enhancements.robust_outliers,
                overall_severity: results.dq_enhancements.overall_severity || 'none',
                summary: results.dq_enhancements.summary || ''
              } : undefined}
            />
          )}
          {activeEnhancedTab === 'anomaly' && journeyId && (
            <AnomalyDetectionPanel journeyId={journeyId} />
          )}
          {activeEnhancedTab === 'drift' && journeyId && (
            <DriftDetectionPanel journeyId={journeyId} />
          )}
          {activeEnhancedTab === 'textual' && journeyId && (
            <TextualQualityPanel journeyId={journeyId} />
          )}
        </div>
      )}

      {/* Category Explanation Drawer */}
      <DataQualityCategoryDrawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        categoryDetail={categoryDetail}
        loading={drawerLoading}
        error={drawerError}
      />
    </div>
  );
};

export default DataQualityScorecard;
